import { SWRConfig } from 'swr';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import serverStore from '../lib/utils/serverStore';
import { settingService, alertService, cartSaleor, cartService } from '../lib/services';
import { axiosInstance, displayRecipient } from '../lib/utils';
import { useAppContext } from '../lib/context';
import { Button, Title } from '../lib/components';
import { setGraphContext } from '../apollo-client';
import { AiOutlineClose } from 'react-icons/ai';
import { customerSaleor } from './../lib/services/saleor/customer.saleor.service';
import Head from 'next/head';

export default function Abonnement({ fallback, carts, alerts, user }) {
  const { addAlert } = useAppContext();
  const router = useRouter();
  const [cartList, setCart] = useState(carts);

  useEffect(() => {
    addAlert(alerts);
  }, [alerts])

  const submitFunction = async (values) => {
    try {
      router.push('/commander');
    } catch (err) {
      addAlert({
        status: err?.response?.data?.status,
        msg: err?.response?.data?.msg,
      });
    }
  };

  const deleteLine = async (id) => {
    try {
      const cart = await cartService.removeFromCart(id);
      setCart(cart);
    } catch (err) {
      addAlert({
        status: err?.response?.data?.status,
        msg: err?.response?.data?.msg,
      });
    }
  }

  return (
    <div className="py-2 flex-1">
      <SWRConfig value={{ fallback }}>
        <Head>
          <title>Animation & Éducation - Panier</title>
          <meta name="description" content="Votre panier pour retrouver tous les produits que vous souhaitez commander !"></meta>
        </Head>
        <section className="mx-5 lg:mx-20 xl:mx-40">
          <Title>
            Votre panier
          </Title>
          <div className="grid grid-cols-12 gap-8">
            <div className='col-span-12 xl:col-span-9'>
              {cartList && cartList.lines &&
                <>
                  <table className="hidden lg:table w-full border-collapse bg-white text-left">
                    <thead className="bg-gray-100">
                      <tr>
                        <td className='px-6 py-4 font-medium text-gray-900'>Description du produit</td>
                        <td className='px-6 py-4 font-medium text-gray-900'>Prix unitaire</td>
                        <td className='px-6 py-4 font-medium text-gray-900'>Qté</td>
                        <td className='px-6 py-4 font-medium text-gray-900'>Total</td>
                        <td></td>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100 border-t border-gray-100'>
                      {cartList && cartList.lines && cartList?.lines.map(line => {
                        return (
                          <tr className="transition ease-in-out" key={line.id}>
                            <td className="p-4">
                              <div className='flex flex-row space-x-4'>
                                <div className='w-32'>
                                  <img className="h-auto max-w-full" src={line?.variant?.product?.thumbnail.url} alt={line?.variant?.product?.thumbnail.alt}></img>
                                </div>
                                <div>
                                  {line?.metadata?.find(el => el.key === "LIB")?.value}
                                  {displayRecipient(line, user)}
                                </div>
                              </div>
                            </td>
                            <td className="text-center py-2 align-baseline">
                              {line?.unitPrice?.gross?.amount?.toFixed(2)} {line?.metadata?.find(el => el.key === "CURRENCY")?.value}
                            </td>
                            <td className="text-center py-2 align-baseline">
                              {line?.quantity}
                            </td>
                            <td className="text-center py-2 align-baseline">
                              {line?.totalPrice?.gross?.amount?.toFixed(2)} {line?.metadata?.find(el => el.key === "CURRENCY")?.value}
                            </td>
                            <td className="px-5 py-2 align-baseline" align="right">
                              <AiOutlineClose className="cursor-pointer hover:text-blue-contrast transition duration-300 ease-out" onClick={() => deleteLine(line.id)} />
                            </td>
                          </tr>
                        );
                      })}
                      {(!cartList || cartList.lines.length === 0) &&
                        <tr>
                          <td className="px-5 py-2" colSpan="5">
                            Vous n'avez aucun produit dans votre panier
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                  <div className="block lg:hidden">
                    <div className='px-6 py-4 font-medium bg-gray-100 text-gray-900'>
                      Description du produit
                    </div>
                    {cartList && cartList.lines && cartList?.lines.map(line => {
                      return (
                        <div>
                          <div className='flex flex-row space-x-4 mt-4'>
                            <div className='w-32'>
                              <img className="h-auto max-w-full" src={line?.variant?.product?.thumbnail.url} alt={line?.variant?.product?.thumbnail.alt}></img>
                            </div>
                            <div>
                              {line?.metadata?.find(el => el.key === "LIB")?.value}
                              {displayRecipient(line, user)}
                            </div>
                          </div>
                          <div className="flex justify-between py-4 text-center">
                            <div>
                              <p className='font-medium text-gray-900'>Prix unitaire</p>
                              {line?.unitPrice?.gross?.amount?.toFixed(2)} {line?.metadata?.find(el => el.key === "CURRENCY")?.value}
                            </div>
                            <div>
                              <p className='font-medium text-gray-900'>Qté</p>
                              {line?.quantity}
                            </div>
                            <div>
                              <p className='font-medium text-gray-900'>Total</p>
                              {line?.totalPrice?.gross?.amount?.toFixed(2)} {line?.metadata?.find(el => el.key === "CURRENCY")?.value}
                            </div>
                            <div className="self-end">
                              <AiOutlineClose className="cursor-pointer hover:text-blue-contrast transition duration-300 ease-out" onClick={() => deleteLine(line.id)} />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    {(!cartList || cartList.lines.length === 0) &&
                      <div className="px-5 py-2">
                        Vous n'avez aucun produit dans votre panier
                      </div>
                    }
                  </div>
                </>
              }
            </div>
            <div className="col-span-12 xl:col-span-3 space-y-4">
              <div className='bg-green-bg px-5 py-3'>
                <h2 className="font-bold">Récapitulatif</h2>
                <div className="flex justify-between my-10">
                  <p className="text-md">Total</p>
                  <span>{cartList?.totalPrice?.gross?.amount?.toFixed(2)} {cartList?.lines?.[0]?.metadata?.find(el => el.key === "CURRENCY")?.value ?? '€'}</span>
                </div>
              </div>
              <Button
                link={true}
                action={"/commander"}
                text="Commander"
                color="pink"
                responsive={false}
                className="px-10 py-4 text-xl w-full"
              />
            </div>
          </div>
        </section >
      </SWRConfig >
    </div >
  );
}

export const getServerSideProps = async function getServerSideProps({ req, res }) {
  axiosInstance.setAxiosContext(req, res)
  setGraphContext(req, res)
  const user = await customerSaleor.me();

  const setting = serverStore.get('setting');

  const carts = await cartSaleor.checkout(req, res);

  const alerts = alertService.getAlert(req, res);
  alertService.deleteAlert(req, res);

  const fallback = {};
  fallback[settingService.SETTING_GET_SETTING_PATH] = setting;
  return {
    props: { fallback, setting, carts: carts ?? {}, alerts, user },
  };
};
